import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { displayErrorMessage } from 'Helpers/FacetNotificationManager';

import ConfirmationDialog from 'Components/UI/ConfirmationDialog';
import ErrorList from 'Components/UI/ErrorList';
import { useDockedContext } from 'Context/DockedContext';

import withEventTrackingLogEvent from 'EventTracking/withEventTrackingLogEvent';
import getDockOverrides from 'Components/UI/Dock/Overrides';
import DefaultDialogChildComponent from 'Components/UI/Dock/DefaultDialogChildComponent';
import DefaultExpandedChildComponent from 'Components/UI/Dock/DefaultExpandedChildComponent';
import Dialog from './States/Dialog';
import Expanded from './States/Expanded';
import Minimized from './States/Minimized';

function DockedItem({ item, EventTracking }) {
  const [minimized, setMinimized] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [errors, setErrors] = useState(null);
  const { rootNodeProps, logEvent } = EventTracking;
  const dockedContext = useDockedContext();
  const {
    getItem, undockItem, dockItem, setSaved,
  } = dockedContext;
  // Used like this: https://reactjs.org/docs/hooks-faq.html#is-there-something-like-instance-variables
  const closedManuallyRef = useRef();

  // NOTE: This useEffect must explicitly remain a hook with no dependencies so that it only runs on dismount.
  useEffect(() => { // eslint-disable-line arrow-body-style
    return async () => {
      const itm = getItem(item.key);

      if (itm?.collection && item?.autoSave && closedManuallyRef.current !== true) {
        const err = await undockItem(itm.key);

        if (err) {
          displayErrorMessage(err, err.errors);
        }
      }
    };
  }, []);

  const {
    ChildComponentExpanded = DefaultExpandedChildComponent,
    ChildComponentDialog = DefaultDialogChildComponent,
    onSubmit: onSubmitOverride = () => { },
    onSubmitBefore: onSubmitBeforeOverride = () => { },
    onChange: onChangeOverride = () => { },
  } = getDockOverrides(item.key);

  const ChildComponentProps = {
    onSubmitBefore: (resource) => {
      if (onSubmitBeforeOverride) {
        return onSubmitBeforeOverride(dockedContext, item, resource);
      }
      return resource;
    },
    onSubmit: (updatedColl) => {
      const itm = getItem(item.key);
      const [itemResource] = itm.collection;
      // if the current docked resource has not been saved to the server, call the dockItem function
      if (!itemResource.link('self')) {
        dockItem(itm.key, itm.title, updatedColl, {
          metaOverrideName: itm.metaOverrideName,
          autoSave: item.autoSave,
          saved: true,
        });
      } else {
        // otherwise, save the current state and set saved to true.
        // this function needs to get called so the saved state gets updated.
        setSaved(item.key, true);
      }

      if (onSubmitOverride) {
        onSubmitOverride(dockedContext, item, updatedColl);
      }
    },
    onChange: (updatedColl) => {
      if (onChangeOverride) {
        onChangeOverride(dockedContext, item, updatedColl);
      }
    },
    autoSave: true,
  };

  const ActionsProps = {
    isFullScreen,
    minimized,
    onClose: async () => {
      // NOTE: this ref must be set before calling undockItem so that it will be defined on unmount
      closedManuallyRef.current = true;
      const itm = getItem(item.key);
      const err = await undockItem(itm.key);

      if (err) {
        setErrors(err.errors);
        logEvent('Clicked close Docked Item with errors.', {
          error: err,
        });
      } else {
        logEvent('Clicked close on Docked Item.');
      }
    },
    toggleFullScreen: (clickLocation) => {
      setIsFullScreen((current) => {
        logEvent(`Clicked Docked Item ${clickLocation} and set Full Screen to ${!current}`);
        return !current;
      });
    },
    toggleMinimized: (clickLocation, setValue) => {
      setMinimized((current) => {
        if (setValue != null) {
          logEvent(`Clicked Docked Item ${clickLocation} and set Minimize to ${setValue}`);
          return setValue;
        }
        logEvent(`Clicked Docked Item ${clickLocation} and set Minimize to ${!current}`);
        return !current;
      });
    },
  };

  let DockComponent = Expanded;
  let ChildComponent = ChildComponentExpanded;

  if (minimized) {
    DockComponent = Minimized;
    ChildComponent = null;
  } else if (isFullScreen) {
    DockComponent = Dialog;
    ChildComponent = ChildComponentDialog;
  }

  return (
    <>
      <DockComponent
        {...rootNodeProps}
        id={`docked-item-${item.key}`}
        item={item}
        ActionsProps={ActionsProps}
        ChildComponent={ChildComponent}
        ChildComponentProps={{
          ...ChildComponentProps,
          collection: item.collection,
        }}
        test={`${item.key}-docked-item`}
      />
      {errors && (
        <ConfirmationDialog
          onCancel={() => {
            logEvent('Clicked Cancel on Close Docked Item Confirmation Dialog.');
            setErrors(null);
          }}
          title="Errors occurred while saving. You may lose some work. Close anyway?"
          onConfirm={() => {
            logEvent('Clicked Confirm on Close Docked Item Confirmation Dialog.');
            setErrors(null);
            undockItem(item.key, { skipSave: true });
          }}
          showCloseIcon={false}
        >
          <ErrorList
            errors={errors}
            title={null}
          />
        </ConfirmationDialog>
      )}
    </>
  );
}

DockedItem.propTypes = {
  item: PropTypes.object.isRequired,
  EventTracking: PropTypes.object.isRequired,
};

const eventTrackingProperties = {
  nodeName: 'Docked Item',
};
export default withEventTrackingLogEvent(eventTrackingProperties, DockedItem);
